import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, TrendingUp, Coins } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDALS = ["🥇", "🥈", "🥉"];

function RankingList({ items, kind }) {
  if (!items?.length) {
    return (
      <div className="text-center text-sm text-muted-foreground py-6" data-testid={`ranking-empty-${kind}`}>
        Ainda não há dados suficientes esta semana.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div
          key={r.user_id}
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
          data-testid={`ranking-${kind}-${r.rank}`}
        >
          <span className="w-8 text-center text-lg" aria-hidden="true">
            {MEDALS[r.rank - 1] || <span className="text-xs text-muted-foreground font-bold">#{r.rank}</span>}
          </span>
          <Avatar className="w-9 h-9">
            <AvatarImage src={r.picture} />
            <AvatarFallback>{r.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{r.name}</div>
            {r.city && <div className="text-[11px] text-muted-foreground truncate">{r.city}</div>}
          </div>
          <div className="text-right">
            {kind === "earnings" ? (
              <>
                <div className="text-sm font-bold text-primary">R$ {Number(r.earned_brl || 0).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">na semana</div>
              </>
            ) : (
              <>
                <div className="text-sm font-bold text-primary flex items-center gap-1 justify-end">
                  <Coins className="w-3.5 h-3.5" /> {Number(r.coins || 0).toLocaleString("pt-BR")}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">na semana</div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WeeklyRanking() {
  const [earnings, setEarnings] = useState([]);
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [e, c] = await Promise.all([
          axios.get(`${API}/rankings/weekly-earnings?limit=10`),
          axios.get(`${API}/rankings/weekly-coins?limit=10`),
        ]);
        if (!alive) return;
        setEarnings(e.data || []);
        setCoins(c.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  return (
    <Card className="p-5" data-testid="weekly-ranking">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-yellow-400/15 text-yellow-500 flex items-center justify-center">
          <Trophy className="w-4 h-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-yellow-500">Ranking · 7 dias</div>
          <div className="text-sm font-semibold">Quem mais ganhou no FUTURO PDV</div>
        </div>
      </div>
      <Tabs defaultValue="earnings">
        <TabsList className="rounded-full">
          <TabsTrigger value="earnings" data-testid="ranking-tab-earnings" className="rounded-full text-xs gap-1">
            <TrendingUp className="w-3 h-3" /> R$
          </TabsTrigger>
          <TabsTrigger value="coins" data-testid="ranking-tab-coins" className="rounded-full text-xs gap-1">
            <Coins className="w-3 h-3" /> Moedas
          </TabsTrigger>
        </TabsList>
        <TabsContent value="earnings" className="mt-3">
          {loading ? <div className="text-sm text-muted-foreground text-center py-6">Carregando...</div> : <RankingList items={earnings} kind="earnings" />}
        </TabsContent>
        <TabsContent value="coins" className="mt-3">
          {loading ? <div className="text-sm text-muted-foreground text-center py-6">Carregando...</div> : <RankingList items={coins} kind="coins" />}
        </TabsContent>
      </Tabs>
    </Card>
  );
}
